import { findOrg } from '@/api/org-api'
import { ensureCanRunOnboarding } from '@/hooks/onboarding/use-onboarding'
import { organizationKeys } from '@/hooks/organization/query-keys'
import { useAuthStore } from '@/store/auth'
import { useOrgStore } from '@/store/org'
import { UserRole } from '@depanneo/types'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

export const Route = createFileRoute('/_onboarding/org/$orgId/onboarding')({
  validateSearch: z.object({
    fromInvite: z.boolean().optional(),
  }),
  loaderDeps: ({ search: { fromInvite } }) => ({ fromInvite }),
  beforeLoad: ({ params: { orgId } }) => {
    const { organizationId, role } = useAuthStore.getState()

    // Only admins can go through onboarding
    if (role !== UserRole.ADMIN) {
      throw redirect({
        to: '/org/$orgId/dashboard',
        params: { orgId: organizationId! },
      })
    }

    // Admin can only access their own org's onboarding
    if (orgId !== organizationId) {
      throw redirect({
        to: '/org/$orgId/onboarding',
        params: { orgId: organizationId! },
      })
    }
  },
  loader: async ({
    context: { queryClient },
    params: { orgId },
    deps: { fromInvite },
  }) => {
    const org = await queryClient.ensureQueryData({
      queryKey: organizationKeys.detail(orgId),
      queryFn: () => findOrg(orgId),
    })

    // Only the ADMIN Depanneo invited runs the wizard (#2820); any other admin
    // is not onboarding this org. A finished org needs no answer.
    const canRunOnboarding =
      org.onboardingCompletedAt === null &&
      (await ensureCanRunOnboarding(queryClient, orgId))
    const nothingToOnboard = !canRunOnboarding

    // Nothing to onboard -> dashboard. Exception: an admin arriving from an
    // invite link still has no password — the wizard runs step 0 only (name +
    // password) and sends them to the dashboard from there.
    if (nothingToOnboard && !fromInvite) {
      throw redirect({
        to: '/org/$orgId/dashboard',
        params: { orgId },
      })
    }

    // Set the org store so child components can access org data
    useOrgStore.getState().setOrg(org)

    return { orgId, canRunOnboarding }
  },
})
